import type { Action, RouteThunk } from 'redux-first-router';
import { NOT_FOUND, redirect } from 'redux-first-router';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { newPermissions } from '@shared/newPermissions';

import {
  RankRequirementsPage,
  RosterLitePage,
  UnitRosterPage,
  YouthAdvancementProfilePage,
} from './components';
import {
  ROUTE_ROSTER_LITE,
  ROUTE_UNIT_ROSTER,
  ROUTE_UNIT_ROSTER_YOUTH,
  ROUTE_UNIT_ROSTER_YOUTH_RANK,
} from './duck/actions';
import {
  canViewUnitRosterSel,
  canViewYouthAdvancementDetailsSel,
} from './duck/selectors';

// Spread the enriched route action `bag.action` so the NOT_FOUND redirect keeps
// `meta.location`. Otherwise `redirect({ type: NOT_FOUND })` has no
// `meta.location.current` and crashes the location reducer
const deny = (
  dispatch: Parameters<RouteThunk>[0],
  bag: Parameters<RouteThunk>[2],
) =>
  dispatch(
    redirect({
      ...(bag.action as Action),
      type: NOT_FOUND,
      payload: { code: 403 },
    }),
  );

/**
 * Restricts the youth/parent unit-roster routes
 * Waits for bootstrap (`ready`) so a cold deep-link doesn't 403 before user data is fetched
 */
const unitRosterThunk: RouteThunk = (dispatch, getState, bag) => {
  const { ready, allowed } = canViewUnitRosterSel(getState());
  if (!ready) return;

  const enabled = featureFlags.getFlag('SBL_6895_UNIT_ROSTER');
  if (!enabled || !allowed) deny(dispatch, bag);
};

/**
 * This is a thunk rather than a route `permission` this needs to be read at runtime
 * depending on who the viewer is
 */
const youthAdvancementThunk: RouteThunk = (dispatch, getState, bag) => {
  const state = getState();
  const { ready, allowed } = canViewUnitRosterSel(state);
  if (!ready) return;

  const enabled = featureFlags.getFlag('SBL_6895_UNIT_ROSTER');
  if (!enabled || !allowed || !canViewYouthAdvancementDetailsSel(state)) {
    deny(dispatch, bag);
  }
};

const routes = {
  [ROUTE_ROSTER_LITE]: {
    path: '/roster-lite/:orgGuid',
    Component: RosterLitePage,
    titleId: 'page.rosterLite',
    permission: newPermissions.VIEW_ROSTER_LITE,
  },
  [ROUTE_UNIT_ROSTER]: {
    path: '/unit-roster/:orgGuid',
    Component: UnitRosterPage,
    titleId: 'page.unitRoster',
    thunk: unitRosterThunk,
  },
  [ROUTE_UNIT_ROSTER_YOUTH]: {
    path: '/unit-roster/:orgGuid/:userId',
    Component: YouthAdvancementProfilePage,
    titleId: 'page.unitRoster',
    // Only users who may edit/approve/award advancements see this screen
    thunk: youthAdvancementThunk,
  },
  [ROUTE_UNIT_ROSTER_YOUTH_RANK]: {
    path: '/unit-roster/:orgGuid/:userId/rank/:rankId',
    Component: RankRequirementsPage,
    titleId: 'page.unitRoster',
    // Same gate as the profile it is opened from
    thunk: youthAdvancementThunk,
  },
};

export default routes;
